import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../utils/helpers";

// store.settings.payment_modes is the same per-store enabled-methods object
// denzo-pos reads in components/UI/OrderArea/PaymentSection.js (keys: card,
// UPI, cash, credit, talabat) — 'talabat' is a delivery-marketplace payment
// mode that doesn't apply to a driver collecting COD at the door, so it's
// deliberately left out here.
const METHODS = [
  { value: "cash", label: "Cash", settingsKey: "cash" },
  { value: "card", label: "Card", settingsKey: "card" },
  { value: "upi", label: "UPI", settingsKey: "UPI" },
  { value: "credit", label: "Credit", settingsKey: "credit" },
];

export default function PaymentDialog({ order, currency, onClose, onConfirm }) {
  const { store } = useAuth();
  const paymentModes = store?.settings?.payment_modes || {};
  const available = METHODS.filter((m) => paymentModes[m.settingsKey]);

  const [method, setMethod] = useState(() => {
    const current = order?.offline_payment_option;
    if (current && available.some((m) => m.value === current)) return current;
    return available[0]?.value || "cash";
  });
  const [received, setReceived] = useState(() => String(order?.grand_total ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!order) return null;

  const balance = (parseFloat(received || 0) - Number(order.grand_total || 0)).toFixed(2);

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await onConfirm({ payment_method: method, received: parseFloat(received || 0) });
    } catch (err) {
      setError(err.message || "Failed to record payment");
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Collect Payment — #{order.order_no || order.id}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        )}
        <ToggleButtonGroup value={method} exclusive onChange={(_, v) => v && setMethod(v)} fullWidth sx={{ mb: 2, flexWrap: "wrap" }}>
          {available.map((m) => (
            <ToggleButton key={m.value} value={m.value}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          label="Received Amount"
          type="number"
          fullWidth
          value={received}
          onChange={(e) => setReceived(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          onClick={() => setReceived(String(order.grand_total ?? ""))}
          sx={{ mb: 1.5 }}
        >
          Received full amount
        </Button>
        <Typography variant="body2" color="text.secondary">
          Bill: {formatPrice(order.grand_total, currency)} · Balance: {formatPrice(balance, currency)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" color="success" onClick={handleConfirm} disabled={saving || !method}>
          {saving ? "Saving..." : "Mark as Delivered"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
